import { useCallback, useMemo, useRef, useState } from 'react';
import { useWizardAssignableAssets } from '~/app/hooks/maintenance/usePlanAssets';
import { PreventiveMaintenanceService } from '~/app/services/preventive-maintenance/preventive-maintenance.service';
import { WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE } from '~/app/services/preventive-maintenance/wizard-assignable-assets.params';
import type { WizardConflictDecision, WizardData } from '../../types';
import type { StepAssetsFilterState } from '../types';
import {
  emptyStepAssetsFilters,
  mergeSelectedAssetIds,
  normalizeDatePickerString,
  sameIdSet,
  toAssignableAssetFilters,
  unresolvedConflictCount,
  applyConflictDecisionsToWizard,
} from '../utils';
import { useStepAssetsColumns } from './useStepAssetsColumns';
import { useStepAssetsFilterOptions } from './useStepAssetsFilterOptions';

export function useStepAssets({
  data,
  onChange,
  conflictModalOpen,
  onConflictModalOpenChange,
}: {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
  conflictModalOpen: boolean;
  onConflictModalOpenChange: (open: boolean) => void;
}) {
  const [skipAssets, setSkipAssets] = useState(data.assetIds.length === 0);
  const [filters, setFilters] = useState<StepAssetsFilterState>(() =>
    emptyStepAssetsFilters(),
  );
  const [page, setPage] = useState(1);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  // Rebuild on every render from current multi-select state (cheap + always fresh).
  const queryFilters = toAssignableAssetFilters(filters);
  const pagination = {
    page,
    limit: WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
  };

  const { assignableAssets, meta, isLoading, isFetching } =
    useWizardAssignableAssets(queryFilters, pagination, {
      enabled: !skipAssets,
    });
  const filterOptions = useStepAssetsFilterOptions(filters, {
    enabled: !skipAssets,
  });
  const { assetColumns, conflictColumns } = useStepAssetsColumns();

  const filteredMatchingCount = meta.total;

  const unresolvedCount = useMemo(
    () =>
      unresolvedConflictCount(data.checkedConflicts, data.conflictDecisions),
    [data.checkedConflicts, data.conflictDecisions],
  );

  const resetAssignmentState = useCallback(
    (patch: Partial<WizardData> = {}) => {
      onChange({
        replaceAssetIds: [],
        checkedConflicts: [],
        conflictDecisions: {},
        conflictsChecked: false,
        ...patch,
      });
    },
    [onChange],
  );

  const handleSkipChange = useCallback(
    (checked: boolean) => {
      setSkipAssets(checked);
      setPage(1);
      if (checked) {
        resetAssignmentState({
          assetIds: [],
          startDate: '',
        });
      }
    },
    [resetAssignmentState],
  );

  const handleFilterChange = useCallback(
    (patch: Partial<StepAssetsFilterState>) => {
      setPage(1);
      setFilters((prev) => {
        const next = { ...prev, ...patch };
        if (
          patch.assetTypeIds !== undefined &&
          !sameIdSet(patch.assetTypeIds, prev.assetTypeIds)
        ) {
          next.assetModelIds = [];
        }
        return next;
      });
    },
    [],
  );

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(Math.max(1, nextPage));
  }, []);

  const handleStartDateChange = useCallback(
    (dateString: string | string[] | null | undefined) => {
      resetAssignmentState({
        startDate: normalizeDatePickerString(dateString),
      });
    },
    [resetAssignmentState],
  );

  const handleSelectionChange = useCallback(
    (selectedIds: string[]) => {
      resetAssignmentState({ assetIds: selectedIds });
    },
    [resetAssignmentState],
  );

  const handleSelectAllMatching = useCallback(async () => {
    setIsSelectingAll(true);
    try {
      const ids =
        await PreventiveMaintenanceService.findWizardAssignableAssetIds(
          queryFilters,
        );
      resetAssignmentState({
        assetIds: mergeSelectedAssetIds(data.assetIds, ids),
      });
    } finally {
      setIsSelectingAll(false);
    }
  }, [data.assetIds, queryFilters, resetAssignmentState]);

  const handleClearSelection = useCallback(() => {
    resetAssignmentState({ assetIds: [] });
  }, [resetAssignmentState]);

  const conflictDecisionsRef = useRef(data.conflictDecisions);
  conflictDecisionsRef.current = data.conflictDecisions;

  const setDecision = useCallback(
    (assetId: string, decision: WizardConflictDecision) => {
      const next = {
        ...conflictDecisionsRef.current,
        [assetId]: decision,
      };
      conflictDecisionsRef.current = next;
      onChange({ conflictDecisions: next });
    },
    [onChange],
  );

  const applyConflictDecisions = useCallback(() => {
    onChange(applyConflictDecisionsToWizard(data));
    onConflictModalOpenChange(false);
  }, [data, onChange, onConflictModalOpenChange]);

  return {
    skipAssets,
    filters,
    assignableAssets,
    page,
    pageSize: WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
    totalMatching: filteredMatchingCount,
    isLoading: isLoading || isFetching || isSelectingAll,
    filteredMatchingCount,
    unresolvedCount,
    conflictModalOpen,
    assetColumns,
    conflictColumns,
    filterOptions,
    handleSkipChange,
    handleFilterChange,
    handlePageChange,
    handleStartDateChange,
    handleSelectionChange,
    handleSelectAllMatching,
    handleClearSelection,
    setDecision,
    applyConflictDecisions,
    onConflictModalOpenChange,
  };
}
